import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { RiAlertLine, RiFilterLine, RiFilterOffLine } from '@remixicon/react';
import { isNil } from 'lodash';
import { type ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { roundToTwoDecimals } from 'shared/utils/numbers';
import LoadingButton from 'src/components/atoms/buttons/LoadingButton';
import Hint from 'src/components/atoms/Hint';
import InventoryQuantityTextField from 'src/components/atoms/InventoryQuantityTextField';
import Link from 'src/components/atoms/Link';
import { AgButton, AgInput, AgText } from 'src/components/ui';
import { AbortSignal, useDebounce } from 'src/hooks/useDebounce';
import useSystemUnitRounding from 'src/hooks/useSystemUnitRounding';
import { notify } from 'src/lib/notify';
import { appLinks } from 'src/routing/appLinks';
import capNumberToDecimal from 'src/utils/capNumberToDecimal';
import fuzzyComplete from 'src/utils/fuzzyComplete';
import type { FindPalletTagsWithLocation } from 'types/graphql';

import { useFindLotPalletTags } from '../hooks/useFindLotPalletTags';
import type { PalletTagPickerData, PalletTagPickerVariant } from './palletTagPickerVariants';

type PalletTagOption = NonNullable<NonNullable<NonNullable<FindPalletTagsWithLocation['lot']>['palletTags']>[number]>;

type PalletTagSelection = { quantity: number; tagNumber: string; weight?: number | null };

const palletTagOptions = (data: PalletTagPickerData | undefined): readonly (PalletTagOption | null)[] =>
  data?.lot?.palletTags ?? [];

export type PalletTagPickerLot = { id?: number | null; slug: string; warehouseId?: number | null };

export type PalletTagPickerInput = {
  palletTag: { id: number; tagNumber: string; weight?: number | null };
  quantity?: number | null;
  weight?: number | null;
};

export type PalletTagPickerSelection = {
  quantity: number;
  weight: number | null | undefined;
  palletTag: { id: number; tagNumber: string };
};

type PalletTagPickerModalProps = {
  variant: PalletTagPickerVariant;
  open: boolean;
  lot: PalletTagPickerLot | null | undefined;
  /** Identity of the row or component being edited; a new key re-checks the lot fetch and re-seeds. */
  fetchKey: object | undefined;
  initialInputs: readonly PalletTagPickerInput[] | null | undefined;
  inputByWeight?: boolean | null;
  unitsToCompare?: number | null;
  canEdit: boolean;
  onClose: () => void;
  onSave: (selections: PalletTagPickerSelection[], syncInput: boolean) => Promise<unknown>;
};

const PROMPT_LAYOUTS = {
  flexRow: {
    sx: { mb: 1, width: '100%', display: 'flex', gap: 0.5 },
    text: 'Select count from pallet tags that will be sold from',
  },
  inline: { sx: { mb: 1, width: '400px' }, text: 'Select count from pallet tags that will be sold from ' },
} as const;

export default function PalletTagPickerModal({
  variant,
  open,
  lot,
  fetchKey,
  initialInputs,
  inputByWeight,
  unitsToCompare,
  canEdit,
  onClose,
  onSave,
}: PalletTagPickerModalProps) {
  const [filter, setFilter] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [palletTags, setPalletTags] = useState<PalletTagOption[]>([]);
  const [showOnlySelected, setShowOnlySelected] = useState(!canEdit);
  const [loading, setLoading] = useState(false);
  const [syncInput, setSyncInput] = useState(true);

  const [selectedTags, setSelectedTags] = useState<Record<number, PalletTagSelection>>({});

  // The modal stays mounted while its owner changes state under it (a production run goes
  // DRAFT -> IN_PROGRESS), so re-derive the read-only "selected only" default on every open.
  useEffect(() => {
    if (open) {
      setShowOnlySelected(!canEdit);
    }
  }, [open, canEdit]);

  const { findPalletTags, variables } = useFindLotPalletTags(variant.query);

  useEffect(() => {
    if (variant.fetchMode !== 'onLotChange' || !fetchKey || !lot?.id) {
      return;
    }

    const shouldFetch = variables?.id !== lot.id;

    if (shouldFetch) {
      setLoading(true);

      findPalletTags({ id: lot.id }).then(({ data }) => {
        setLoading(false);
        setPalletTags(
          palletTagOptions(data).filter(
            (palletTag): palletTag is PalletTagOption =>
              palletTag !== null && (canEdit ? !!palletTag.cartonQuantity : true)
          )
        );
      });
    }
  }, [variant, variables, fetchKey, lot, findPalletTags, canEdit]);

  const selectedTagsRef = useRef(selectedTags);

  useEffect(() => {
    selectedTagsRef.current = selectedTags;
  }, [selectedTags]);

  const fetchedLotId = useRef<number | null>(null);

  useEffect(() => {
    if (variant.fetchMode !== 'onLotChangeOrWhenEmpty') {
      return;
    }

    if (!fetchKey) {
      if (!loading && !palletTags.length) {
        fetchedLotId.current = null;
      }
      return;
    }

    if (!lot?.id || fetchedLotId.current === lot.id) {
      return;
    }

    fetchedLotId.current = lot.id;
    setLoading(true);

    findPalletTags({ id: lot.id }).then(({ data }) => {
      setLoading(false);
      setPalletTags(
        palletTagOptions(data).filter(
          (palletTag): palletTag is PalletTagOption =>
            palletTag !== null && (canEdit ? !!palletTag.cartonQuantity : palletTag.id in selectedTagsRef.current)
        )
      );
    });
  }, [variant, fetchKey, lot, findPalletTags, canEdit, loading, palletTags.length]);

  useEffect(() => {
    if (!fetchKey || !initialInputs) {
      return;
    }

    setSelectedTags(
      // convert the array of pallet tags to a record of tag id to quantity
      initialInputs.reduce<Record<number, PalletTagSelection>>((acc, { palletTag, quantity, weight }) => {
        if (!quantity) {
          return acc;
        }

        return {
          ...acc,
          [palletTag.id]: { quantity, tagNumber: palletTag.tagNumber, weight: weight ?? palletTag.weight },
        };
      }, {})
    );
  }, [fetchKey, initialInputs]);

  const fuzzyFilter = fuzzyComplete(['tagNumber']);

  const filteredTags = useMemo((): PalletTagOption[] => {
    return fuzzyFilter(palletTags, { inputValue: filter }).filter(
      (tag: PalletTagOption) => !showOnlySelected || selectedTags[tag.id]?.quantity > 0
    );
  }, [filter, fuzzyFilter, showOnlySelected, selectedTags, palletTags]);

  const noTags = filteredTags.length === 0;

  const containerRef = useRef<HTMLDivElement>(null);

  const scrollToTag = (tag: string) => {
    const container = containerRef.current;
    if (container) {
      const children = container.children;

      for (let i = 0; i < children.length; i++) {
        if (children[i].textContent?.includes(tag)) {
          children[i].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          break;
        }
      }
    }
  };

  // debounce the filter value so that the scanner does not trigger the filter.
  // this prevents jumpy behavior which is caused by the filter clearing after scanning.
  // Since the filter is debounced, we need more granular control over whether it triggers or not,
  // so we use an abort signal to cancel the debounce.
  const abortSignal = useRef<AbortSignal>(null);
  const debouncedSetFilter = useDebounce(
    (value) => {
      setFilter(value);
    },
    100,
    abortSignal
  );

  const unitRounding = useSystemUnitRounding();

  const totalQuantityPicked = useMemo(() => {
    const displayed = (quantity: number) =>
      variant.quantityInput === 'inventoryQuantity' || variant.roundQuantityOnSave ? unitRounding(quantity) : quantity;

    // Round the sum so float drift (1.1 + 2.2) cannot read as an over-pick of a 3.3 target.
    return roundToTwoDecimals(Object.values(selectedTags).reduce((a, b) => a + displayed(b?.quantity || 0), 0) || 0);
  }, [variant, selectedTags, unitRounding]);

  const pickTarget = isNil(unitsToCompare) ? null : Number(unitsToCompare);
  const isOverPicked = pickTarget !== null && totalQuantityPicked > pickTarget;

  const FilterIcon = showOnlySelected ? RiFilterLine : RiFilterOffLine;
  const lotWarehouseId = lot?.warehouseId;
  const lotSlug = lot?.slug;
  const lotTrackingLink =
    lotSlug && lotWarehouseId ? (
      <Link
        label={`Lot #${lotSlug}`}
        {...appLinks.viewLotTracking({ slug: lotSlug, warehouseId: lotWarehouseId })}
        sx={variant.lotLinkSx}
      />
    ) : (
      <Link label={`Lot #${lotSlug}`} sx={variant.lotLinkSx} />
    );
  const prompt = PROMPT_LAYOUTS[variant.promptLayout];

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Pick from Pallet Tags</DialogTitle>
      <DialogContent>
        <DialogContentText sx={prompt.sx}>
          {prompt.text}
          {lotTrackingLink}
        </DialogContentText>
        <Box sx={{ display: 'flex' }}>
          <TextField
            fullWidth
            variant="standard"
            label="Search or Enter Tag"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);

              debouncedSetFilter(e.target.value);
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <Hint title="Filter selected pallet tags">
                    <IconButton
                      onClick={() => {
                        setShowOnlySelected(!showOnlySelected);
                      }}
                      size="small"
                    >
                      <FilterIcon size={20} />
                    </IconButton>
                  </Hint>
                ),
              },
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const matchingTag = filteredTags.find(({ tagNumber }) => tagNumber === inputValue);

                if (matchingTag && canEdit) {
                  if (!selectedTags[matchingTag.id]) {
                    setSelectedTags({
                      ...selectedTags,
                      [matchingTag.id]: { quantity: 0, tagNumber: matchingTag.tagNumber, weight: matchingTag.weight },
                    });

                    notify.success(`Added tag #${matchingTag.tagNumber}`);

                    setInputValue('');
                    setFilter('');

                    // abort the filter debounce so that the value does not get overwritten
                    abortSignal.current = 'abort';
                  }

                  setTimeout(() => {
                    scrollToTag(matchingTag.tagNumber);
                  }, 10);
                }
              }
            }}
          />
        </Box>

        <Box ref={containerRef} sx={{ height: '300px', overflow: 'scroll', display: 'flex', flexDirection: 'column' }}>
          {!loading && noTags && (
            <AgText size="h6" color="secondary" className="m-auto">
              No Pallet Tags Available
            </AgText>
          )}
          {loading && (
            <Box
              sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <CircularProgress />
            </Box>
          )}
          {!loading &&
            filteredTags.map(({ id, tagNumber, cartonQuantity, weight, warehouseLocation }) => {
              const currentTagQuantity = selectedTags[id]?.quantity;
              const currentTagWeight = selectedTags[id]?.weight;

              const handleUpdateQuantity = (value: string) => {
                const quantity = +capNumberToDecimal(value);

                if (quantity < 0) {
                  return;
                }

                const defaultTagWeight = ((weight ?? 0) / cartonQuantity) * quantity;

                setSelectedTags((currentTagQuantities) => ({
                  ...currentTagQuantities,
                  [id]: { quantity, tagNumber, weight: defaultTagWeight },
                }));
              };

              const handleUpdateWeight = (value: string) => {
                const inputWeight = +capNumberToDecimal(value);

                if (inputWeight < 0) {
                  return;
                }

                const defaultTagQuantity = (cartonQuantity / (weight ?? 0)) * inputWeight;

                setSelectedTags((currentTagQuantities) => ({
                  ...currentTagQuantities,
                  [id]: { quantity: defaultTagQuantity, tagNumber, weight: inputWeight },
                }));
              };

              const handleUpdateTwoDecimalQuantity = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                const value = parseFloat(event.target.value);
                // round to 2 decimal places
                const quantity = Math.round(value * 100) / 100;

                if (quantity < 0) {
                  return;
                }

                setSelectedTags((currentTagQuantities) => ({ ...currentTagQuantities, [id]: { quantity, tagNumber } }));
              };

              const isSelected = currentTagQuantity > 0;

              return (
                <Box key={tagNumber} sx={{ pl: 0, display: 'flex', alignItems: 'center', height: '40px', mr: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      width: '100%',
                      alignItems: 'center',
                      ...(variant.locationColumn && { gap: 2 }),
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Checkbox
                        checked={isSelected}
                        disabled={!canEdit}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedTags({ ...selectedTags, [id]: { quantity: 0, tagNumber, weight: 0 } });
                            return;
                          }

                          const remainingUnits =
                            pickTarget === null ? cartonQuantity : roundToTwoDecimals(pickTarget - totalQuantityPicked);
                          const quantity = Math.min(cartonQuantity, remainingUnits);

                          if (quantity <= 0) {
                            if (pickTarget !== null) {
                              notify.warn(`All ${pickTarget} units on this row are already picked`);
                            }
                            return;
                          }

                          const tagWeight =
                            quantity === cartonQuantity ? weight : ((weight ?? 0) / cartonQuantity) * quantity;

                          setSelectedTags({ ...selectedTags, [id]: { quantity, tagNumber, weight: tagWeight } });
                        }}
                      />
                      <Typography sx={{ fontSize: '14px' }}>#{tagNumber}</Typography>
                    </Box>
                    {variant.locationColumn && (
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography sx={{ fontSize: '14px' }}>{warehouseLocation?.description}</Typography>
                      </Box>
                    )}
                    {variant.quantityInput === 'twoDecimals' ? (
                      <TextField
                        sx={{ width: '160px' }}
                        size="small"
                        type="number"
                        disabled={!canEdit}
                        value={currentTagQuantity ?? 0}
                        error={currentTagQuantity > cartonQuantity}
                        onChange={handleUpdateTwoDecimalQuantity}
                        slotProps={{
                          input: {
                            sx: { fontSize: '13px', display: 'flex' },
                            endAdornment: (
                              <AgText size="12" color="secondary" className="w-full text-right">
                                of {cartonQuantity}
                              </AgText>
                            ),
                          },
                        }}
                      />
                    ) : (
                      <InventoryQuantityTextField
                        className="w-[160px]"
                        size="sm"
                        type="number"
                        value={unitRounding(currentTagQuantity ?? 0)}
                        isDisabled={inputByWeight || !canEdit}
                        isInvalid={currentTagQuantity > cartonQuantity}
                        onChange={handleUpdateQuantity}
                        endContent={
                          <AgText size="12" color="secondary" className="w-full text-right">
                            of {unitRounding(cartonQuantity)}
                          </AgText>
                        }
                      />
                    )}
                    {variant.weight.tracked && (
                      <AgInput
                        className="w-[200px]"
                        size="sm"
                        type="number"
                        value={currentTagWeight?.toString() ?? '0'}
                        isDisabled={!inputByWeight || !canEdit}
                        onChange={handleUpdateWeight}
                        isInvalid={Number(currentTagWeight) < 0}
                        endContent={
                          <AgText size="12" color="secondary" className="w-full text-right">
                            of {weight?.toFixed(2)} lbs
                          </AgText>
                        }
                      />
                    )}
                  </Box>
                </Box>
              );
            })}
        </Box>
        <Divider />
        {variant.footer === 'unitsPickedOfTarget' ? (
          <Box
            sx={{
              p: 1,
              pb: 0,
              color: isOverPicked ? 'error.main' : 'text.primary',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Typography>
              {pickTarget === null
                ? `${totalQuantityPicked} units picked`
                : `${totalQuantityPicked} out of ${pickTarget} units picked`}
            </Typography>
            {isOverPicked && <RiAlertLine size={20} className="ml-0.5" />}
          </Box>
        ) : (
          <Box sx={{ p: 1, pb: 0, display: 'flex', alignItems: 'center' }}>
            <Typography>{totalQuantityPicked} units selected</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {canEdit && variant.weight.syncInputCheckbox && (
            <FormControlLabel
              control={<Checkbox size="small" />}
              checked={syncInput}
              onChange={(_, checked) => {
                setSyncInput(checked);
              }}
              label="Sync input units & weight"
            />
          )}
          <AgButton variant="ghost" onPress={onClose}>
            {canEdit ? variant.dismissLabel.editable : variant.dismissLabel.readOnly}
          </AgButton>
          {canEdit && (
            <Hint title={isOverPicked ? `Picks exceed the row's ${pickTarget} units` : ''}>
              <span>
                <LoadingButton
                  handleRequest={() => {
                    // convert tag quantities into mutation input format
                    const quantityForPalletTags = Object.entries(selectedTags).map(([palletTagId, tagSelection]) => ({
                      quantity: variant.roundQuantityOnSave
                        ? unitRounding(tagSelection.quantity)
                        : tagSelection.quantity,
                      weight: tagSelection.weight,
                      palletTag: { id: parseInt(palletTagId), tagNumber: tagSelection.tagNumber },
                    }));

                    return onSave(quantityForPalletTags, syncInput);
                  }}
                  variant="primary"
                  isDisabled={noTags || isOverPicked}
                >
                  Save
                </LoadingButton>
              </span>
            </Hint>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
